
import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Users, Trophy } from 'lucide-react';
import { Division } from '@/types/division';

interface DivisionTableProps {
  divisions: Division[];
  onEdit: (division: Division) => void;
  onDelete: (divisionId: string) => void;
}

export const DivisionTable = ({ divisions, onEdit, onDelete }: DivisionTableProps) => {
  const getTravelLevelBadge = (travelLevel: string | null | undefined) => {
    if (!travelLevel) return null;
    
    const levelConfig = {
      local: { label: 'Local', variant: 'secondary' as const },
      in_state: { label: 'In-State', variant: 'default' as const },
      out_of_state: { label: 'Out-of-State', variant: 'destructive' as const },
      international: { label: 'International', variant: 'outline' as const },
    };
    
    const config = levelConfig[travelLevel as keyof typeof levelConfig];
    if (!config) return null;
    
    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Division Name</TableHead>
          <TableHead>Travel Level</TableHead>
          <TableHead className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Trophy className="h-4 w-4" />
              Teams
            </div>
          </TableHead>
          <TableHead className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Users className="h-4 w-4" />
              Players
            </div>
          </TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {divisions.map((division) => (
          <TableRow key={division.id}>
            <TableCell className="font-medium">{division.name}</TableCell>
            <TableCell>
              {getTravelLevelBadge(division.travel_level) || (
                <span className="text-muted-foreground">None</span>
              )}
            </TableCell>
            <TableCell className="text-center">
              <span className="font-medium text-lg">{division.team_count || 0}</span>
            </TableCell>
            <TableCell className="text-center">
              <span className="font-medium text-lg">{division.player_count || 0}</span>
            </TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(division)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(division.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
